import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy, Search } from "lucide-react";
import api, { formatApiError, assetUrl } from "../../lib/api";
import SEO from "../../components/site/SEO";
import { inputCls, labelCls, btnPrimaire, btnSecondaire, confirmerSuppression } from "./ui";

const VIDE = {
  marque: "", modele: "", largeur: "", hauteur: "", diametre: "",
  indice_charge: "", indice_vitesse: "", saison: "Été", etat: "Neuf",
  prix: "", photo: "", disponible: true,
};

const SAISONS = ["Été", "Hiver", "4 saisons"];
const ETATS = ["Neuf", "Occasion"];
const dimension = (p) => `${p.largeur}/${p.hauteur}/R${p.diametre} ${p.indice_charge}${p.indice_vitesse}`;

export default function AdminCatalogue() {
  const [pneus, setPneus] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [recherche, setRecherche] = useState("");
  const [edition, setEdition] = useState(null);
  const [visible, setVisible] = useState(true);

  const charger = () => api.get("/admin/pneus").then((r) => setPneus(r.data)).catch(() => {});
  useEffect(() => {
    charger();
    api.get("/admin/photos").then((r) => setPhotos(r.data)).catch(() => {});
    api.get("/admin/parametres").then((r) => setVisible(r.data.catalogue_visible)).catch(() => {});
  }, []);

  const basculerCatalogue = async () => {
    try {
      await api.put("/admin/parametres", { catalogue_visible: !visible });
      setVisible(!visible);
      toast.success(!visible
        ? "Le catalogue est maintenant visible sur le site."
        : "Le catalogue est masqué sur le site. Vos fiches sont conservées et restent modifiables ici.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const marques = [...new Set(pneus.map((p) => p.marque))].sort();

  const resultats = pneus.filter((p) =>
    `${p.marque} ${p.modele} ${dimension(p)}`.toLowerCase().includes(recherche.toLowerCase())
  );

  const enregistrer = async (e) => {
    e.preventDefault();
    const payload = { ...edition, prix: Number(edition.prix) || 0 };
    try {
      if (edition.id) {
        await api.put(`/admin/pneus/${edition.id}`, payload);
        toast.success("Le pneu a été modifié.");
      } else {
        await api.post("/admin/pneus", payload);
        toast.success("Le pneu est en ligne dans le catalogue.");
      }
      setEdition(null);
      charger();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const dupliquer = async (p) => {
    const res = await api.post(`/admin/pneus/${p.id}/dupliquer`);
    toast.success("Pneu dupliqué. Modifiez la dimension ci-dessous.");
    charger();
    setEdition(res.data);
  };

  const supprimer = async (p) => {
    if (!confirmerSuppression(`${p.marque} ${dimension(p)}`)) return;
    await api.delete(`/admin/pneus/${p.id}`);
    toast.success("Pneu retiré du catalogue.");
    charger();
  };

  const basculerDispo = async (p) => {
    await api.put(`/admin/pneus/${p.id}`, { ...p, disponible: !p.disponible });
    toast.success(p.disponible ? "Pneu masqué du site." : "Pneu de nouveau visible sur le site.");
    charger();
  };

  if (edition) {
    const choix = (champ, options) => (
      <div className="flex gap-2" role="group" aria-label={champ}>
        {options.map((o) => (
          <button key={o} type="button" data-testid={`pneu-${champ}-${o}`}
            onClick={() => setEdition({ ...edition, [champ.toLowerCase() === "saison" ? "saison" : "etat"]: o })}
            aria-pressed={edition[champ === "Saison" ? "saison" : "etat"] === o}
            className={`flex-1 font-heading font-semibold uppercase tracking-wider text-lg px-4 py-3.5 transition-colors ${
              edition[champ === "Saison" ? "saison" : "etat"] === o
                ? "bg-brand-red text-white" : "bg-brand-light text-brand-dark hover:bg-gray-200"
            }`}>
            {o}
          </button>
        ))}
      </div>
    );

    return (
      <div className="max-w-2xl" data-testid="pneu-formulaire">
        <SEO title="Pneu — Espace gérant | Promo Pneus" description="Ajouter ou modifier un pneu du catalogue." />
        <h1 className="font-heading font-bold uppercase text-3xl text-brand-dark mb-6">
          {edition.id ? "Modifier ce pneu" : "Ajouter un pneu"}
        </h1>
        <form onSubmit={enregistrer} className="space-y-5 bg-white p-6 shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="pneu-marque" className={labelCls}>Marque</label>
              <input id="pneu-marque" data-testid="pneu-marque" required list="marques-connues" className={inputCls}
                value={edition.marque} onChange={(e) => setEdition({ ...edition, marque: e.target.value })}
                placeholder="Ex. : Michelin" />
              <datalist id="marques-connues">
                {marques.map((m) => <option key={m} value={m} />)}
              </datalist>
            </div>
            <div>
              <label htmlFor="pneu-modele" className={labelCls}>Modèle</label>
              <input id="pneu-modele" data-testid="pneu-modele" className={inputCls}
                value={edition.modele} onChange={(e) => setEdition({ ...edition, modele: e.target.value })}
                placeholder="Ex. : Primacy 4" />
            </div>
          </div>
          <div>
            <span className={labelCls}>Dimension</span>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label htmlFor="pneu-largeur" className="text-sm text-brand-body">Largeur</label>
                <input id="pneu-largeur" data-testid="pneu-largeur" required className={inputCls}
                  value={edition.largeur} onChange={(e) => setEdition({ ...edition, largeur: e.target.value })} placeholder="195" />
              </div>
              <div>
                <label htmlFor="pneu-hauteur" className="text-sm text-brand-body">Hauteur</label>
                <input id="pneu-hauteur" data-testid="pneu-hauteur" required className={inputCls}
                  value={edition.hauteur} onChange={(e) => setEdition({ ...edition, hauteur: e.target.value })} placeholder="65" />
              </div>
              <div>
                <label htmlFor="pneu-diametre" className="text-sm text-brand-body">Diamètre</label>
                <input id="pneu-diametre" data-testid="pneu-diametre" required className={inputCls}
                  value={edition.diametre} onChange={(e) => setEdition({ ...edition, diametre: e.target.value })} placeholder="15" />
              </div>
            </div>
            <p className="text-sm text-brand-body mt-2">La dimension s'affiche ainsi : 195/65/R15. Elle est écrite sur le flanc du pneu.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="pneu-charge" className={labelCls}>Indice de charge</label>
              <input id="pneu-charge" data-testid="pneu-charge" className={inputCls}
                value={edition.indice_charge} onChange={(e) => setEdition({ ...edition, indice_charge: e.target.value })} placeholder="91" />
            </div>
            <div>
              <label htmlFor="pneu-vitesse" className={labelCls}>Indice de vitesse</label>
              <input id="pneu-vitesse" data-testid="pneu-vitesse" className={inputCls}
                value={edition.indice_vitesse} onChange={(e) => setEdition({ ...edition, indice_vitesse: e.target.value })} placeholder="V" />
            </div>
          </div>
          <div>
            <span className={labelCls}>Saison</span>
            {choix("Saison", SAISONS)}
          </div>
          <div>
            <span className={labelCls}>État</span>
            {choix("État", ETATS)}
          </div>
          <div>
            <label htmlFor="pneu-prix" className={labelCls}>Prix indicatif (en euros)</label>
            <input id="pneu-prix" data-testid="pneu-prix" type="number" min="0" step="1" required className={inputCls}
              value={edition.prix} onChange={(e) => setEdition({ ...edition, prix: e.target.value })} placeholder="89" />
          </div>
          <div>
            <label htmlFor="pneu-photo" className={labelCls}>Photo (facultatif)</label>
            <select id="pneu-photo" data-testid="pneu-photo" className={inputCls}
              value={edition.photo} onChange={(e) => setEdition({ ...edition, photo: e.target.value })}>
              <option value="">Photo par défaut</option>
              {photos.map((ph) => <option key={ph.id} value={ph.url}>{ph.nom}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" data-testid="pneu-disponible" className="w-6 h-6 accent-[#E8492F]"
              checked={edition.disponible} onChange={(e) => setEdition({ ...edition, disponible: e.target.checked })} />
            <span className="font-body text-brand-dark text-base">Disponible (visible sur le site)</span>
          </label>
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button type="submit" data-testid="pneu-enregistrer" className={btnPrimaire}>
              {edition.id ? "Enregistrer les modifications" : "Ajouter au catalogue"}
            </button>
            <button type="button" onClick={() => setEdition(null)} className={btnSecondaire}>Annuler</button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-catalogue">
      <SEO title="Catalogue — Espace gérant | Promo Pneus" description="Gérer le catalogue de pneus." />
      <div className="bg-white p-6 shadow-sm border-l-4 border-brand-dark mb-8" data-testid="carte-visibilite-catalogue">
        <h2 className="font-heading font-semibold uppercase text-2xl text-brand-dark mb-2">Catalogue</h2>
        <p className="text-brand-body mb-2">
          Désactivez-le si vous ne souhaitez pas afficher vos pneus pour le moment. Vos fiches produits sont conservées.
        </p>
        <p className="text-brand-body mb-5 font-medium">
          Même masqué, votre catalogue reste entièrement modifiable ici : préparez vos fiches, puis réactivez l'affichage quand vous êtes prêt.
        </p>
        <button
          role="switch"
          aria-checked={visible}
          data-testid="bascule-catalogue-site"
          onClick={basculerCatalogue}
          className={`w-full sm:w-auto font-heading font-semibold uppercase tracking-wider text-lg px-8 py-4 transition-colors ${
            visible ? "bg-green-700 text-white" : "bg-gray-300 text-brand-dark"
          }`}
        >
          Afficher le catalogue sur le site : {visible ? "Oui" : "Non"}
        </button>
      </div>
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <h1 className="font-heading font-bold uppercase text-4xl text-brand-dark tracking-tight">Catalogue</h1>
        <button data-testid="pneu-ajouter" onClick={() => setEdition(VIDE)} className={`${btnPrimaire} inline-flex items-center gap-2`}>
          <Plus size={22} aria-hidden="true" /> Ajouter un pneu
        </button>
      </div>
      <div className="relative mb-6">
        <Search size={22} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-body" aria-hidden="true" />
        <input data-testid="catalogue-recherche" type="search" value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          placeholder="Rechercher un pneu (marque, dimension…)"
          aria-label="Rechercher un pneu"
          className={`${inputCls} pl-12`} />
      </div>
      <p className="text-brand-body mb-4" aria-live="polite">{resultats.length} pneu{resultats.length > 1 ? "s" : ""}</p>
      <div className="space-y-3">
        {resultats.map((p) => (
          <div key={p.id} data-testid={`pneu-ligne-${p.id}`}
            className="bg-white p-4 shadow-sm flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-heading font-semibold uppercase text-xl text-brand-dark">
                {p.marque} {p.modele}
              </p>
              <p className="text-brand-body">
                {dimension(p)} · {p.saison} · {p.etat} · <strong>{p.prix} €</strong>
                {!p.disponible && <span className="ml-2 text-brand-redhover font-semibold">· masqué du site</span>}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button data-testid={`pneu-dispo-${p.id}`} onClick={() => basculerDispo(p)}
                className={`font-heading font-semibold uppercase tracking-wider px-4 py-3 text-base transition-colors ${
                  p.disponible ? "bg-green-700 text-white" : "bg-gray-300 text-brand-dark"
                }`} aria-pressed={p.disponible}>
                {p.disponible ? "Disponible" : "Masqué"}
              </button>
              <button data-testid={`pneu-dupliquer-${p.id}`} onClick={() => dupliquer(p)}
                className="p-3 bg-brand-light hover:bg-gray-200 text-brand-dark transition-colors"
                aria-label={`Dupliquer ${p.marque} ${dimension(p)}`} title="Dupliquer (pratique pour une dimension voisine)">
                <Copy size={20} />
              </button>
              <button data-testid={`pneu-modifier-${p.id}`} onClick={() => setEdition(p)}
                className="p-3 bg-brand-light hover:bg-gray-200 text-brand-dark transition-colors" aria-label={`Modifier ${p.marque} ${dimension(p)}`}>
                <Pencil size={20} />
              </button>
              <button data-testid={`pneu-supprimer-${p.id}`} onClick={() => supprimer(p)}
                className="p-3 bg-red-50 hover:bg-red-100 text-brand-redhover transition-colors" aria-label={`Supprimer ${p.marque} ${dimension(p)}`}>
                <Trash2 size={20} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
